
import { computed, defineComponent, onBeforeUnmount, onMounted, reactive, toRaw } from 'vue'

import ymaps from 'yandex-maps'

// components
import Loader from '@/common/components/UI/Loader/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import RadioGroup from '@/common/components/UI/RadioGroup/index.vue'

// constants
import { YANDEX_MAP_TYPE, TWO_GIS_MAP_TYPE } from '@/core/constants/map.constants'

const MAP_CFG = {
    ZOOM: 10,
    CENTER: [43.25391893213578, 76.92523545812777],
}

const MAP_LAYERS = [
    { id: 1, name: 'Yandex', value: YANDEX_MAP_TYPE },
    { id: 2, name: '2GIS', value: TWO_GIS_MAP_TYPE },
]

interface windowWithYmaps extends Window {
    ymaps: typeof ymaps
}

declare const window: windowWithYmaps

export default defineComponent({
    components: {
        loader: Loader,
        'the-button': TheButton,
        'radio-group': RadioGroup,
    },
    props: {
        mapId: {
            type: String,
            default: 'map',
        },
        twoGis: {
            type: Boolean,
            default: true,
        },
        isCanHide: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['map-ready', 'map-change'],
    setup(props, { emit }) {
        const state = reactive({
            is_loading: false,
            is_failed: false,
            is_opened: true,
            instance: undefined as ymaps.Map | undefined,
            layer: 'yandex#map',
        })

        const openMap = () => {
            state.is_opened = true
        }

        const closeMap = () => {
            state.is_opened = false
        }

        const loadScript = () => {
            return new Promise<void>((resolve, reject) => {
                const MAP_URL = process.env.VUE_APP_MAP_SCRIPT_URL
                const MAP_KEY = process.env.VUE_APP_MAP_KEY
                const MAP_LANG = process.env.VUE_APP_MAP_LANG

                if (!MAP_URL || !MAP_KEY || !MAP_LANG) {
                    const ENV_ERROR = new Error('Not found env for map')
                    reject(ENV_ERROR)
                    return
                }

                const script = document.createElement('script')
                script.id = 'ymaps'
                script.src = `${MAP_URL}?apikey=${MAP_KEY}&lang=${MAP_LANG}`

                script.onerror = (error) => {
                    reject(error)
                }

                script.onload = () => {
                    resolve()
                }

                document.head.append(script)
            })
        }

        const renderMap = () => {
            return new Promise<ymaps.Map>((resolve, reject) => {
                try {
                    window.ymaps.ready(() => {
                        const map = new window.ymaps.Map(props.mapId, {
                            zoom: MAP_CFG.ZOOM,
                            center: MAP_CFG.CENTER,
                            controls: [],
                        })

                        resolve(map)
                    })
                } catch (error) {
                    reject(error)
                }
            })
        }

        const addTwoGisLayer = () => {
            return new Promise<void>((resolve, reject) => {
                try {
                    const layer = function () {
                        // @ts-ignore
                        return new window.ymaps.Layer(
                            'http://tile2.maps.2gis.com/tiles?x=%x&y=%y&z=%z',
                            // @ts-ignore
                            {
                                notFoundTile: ''.concat(
                                    '//tile%d|2.maps.2gis.com/tiles',
                                    '/2/0-0.jpeg'
                                ),
                                // @ts-ignore
                                projection: window.ymaps.projection.sphericalMercator,
                            }
                        )
                    }

                    // @ts-ignore
                    window.ymaps.layer.storage.add('2gis#layer', layer)
                    // @ts-ignore
                    window.ymaps.mapType.storage.add(
                        '2gis#map',
                        new window.ymaps.MapType('2gis#map', ['2gis#layer'])
                    )

                    resolve()
                } catch (error) {
                    reject(error)
                }
            })
        }

        const isMapLoaded = computed(() => {
            return Boolean(!state.is_loading && !state.is_failed && Boolean(state.instance))
        })

        const isMapShowed = computed(() => {
            return Boolean(state.is_opened && !state.is_loading && !state.is_failed)
        })

        const isMapScriptLoaded = computed(() => {
            const script = document.head.querySelector('script#ymaps')
            return Boolean(script)
        })

        const loadMap = async () => {
            try {
                state.is_loading = true

                if (!isMapScriptLoaded.value) await loadScript()
                state.instance = await renderMap()

                if (props.twoGis) {
                    await addTwoGisLayer()
                }

                emit('map-ready', state.instance)

                state.is_failed = false
            } catch (error) {
                console.error(error)
                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        onMounted(() => loadMap())

        onBeforeUnmount(() => {
            const script = document.head.querySelector('script#ymaps')

            if (script) {
                script.remove()
            }

            // @ts-ignore
            if (window.ymaps) window.ymaps = undefined
        })

        const switchMap = (map: string) => {
            if (!props.twoGis || !state.instance) return

            const instance = toRaw(state.instance)
            instance.setType(map)
            emit('map-change', map)
        }

        return { MAP_LAYERS, state, openMap, closeMap, isMapLoaded, isMapShowed, switchMap }
    },
})
